/* eslint-disable jsx-a11y/label-has-associated-control */
import { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/catalyst/button';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from '@/components/ui/catalyst/dialog';
import { Input } from '@/components/ui/catalyst/input';
import { useCreateAgentChatWorkspaceMutation } from '@/services/api/agentChatWorkspaceService';
import { ExhibitLabelFormat } from '@/services/types/agent-chat-workspace-types';

interface CreateWorkspaceDialogProps {
  isOpen: boolean;
  onClose: () => void;
  matterId: string;
}

export const CreateWorkspaceDialog = ({
  isOpen,
  onClose,
  matterId,
}: CreateWorkspaceDialogProps) => {
  const [createWorkspace, { isLoading }] =
    useCreateAgentChatWorkspaceMutation();

  const [name, setName] = useState('');

  const resetForm = () => {
    setName('');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      await createWorkspace({
        matterId,
        data: {
          name,
          disable_exhibit_pages: false,
          exhibit_label_format: ExhibitLabelFormat.ALPHABETIC,
        },
      }).unwrap();

      // Reset form and close dialog
      resetForm();
      onClose();
    } catch (error) {
      toast.error('Failed to create workspace');
    }
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} className="max-w-md mx-auto">
      <div className="p-6">
        <DialogTitle>Create New Workspace</DialogTitle>

        <DialogBody>
          <form onSubmit={handleSubmit} id="create-workspace-form">
            <div className="space-y-4">
              <div className="form-group">
                <label htmlFor="create-name" className="block mb-2 font-medium">
                  Name <span className="text-red-500">*</span>
                </label>
                <Input
                  id="create-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  placeholder="Enter workspace name"
                  className="w-full"
                />
              </div>
            </div>
          </form>
        </DialogBody>

        <DialogActions>
          <Button
            type="button"
            onClick={handleClose}
            disabled={isLoading}
            className="bg-gray-200 hover:bg-gray-300"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="create-workspace-form"
            disabled={!name || isLoading}
            className={`bg-blue-500 text-white ${isLoading ? 'opacity-70 cursor-not-allowed' : 'hover:bg-blue-600'}`}
          >
            {isLoading ? 'Creating...' : 'Create'}
          </Button>
        </DialogActions>
      </div>
    </Dialog>
  );
};
